import { useEffect, useState } from "react";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import LookupAPI from "../../QueueBoard/api/lookupDataApi";
import { updateLookup, updateLookupRecord } from "./fetchCatch";
import { SettingsLocation } from "../../../models/lookupTypes";
import {
  ApplicationUser,
  WorkOrderStatus,
  WorkOrderPriorityCode,
  ServiceCodes,
  CustomerListHeader,
  PaymentMethod,
  AltStockLocation,
  PartListHeader,
  UnitListHeader,
  MiscCharge,
  Zone,
  Country,
  State,
} from "../../../models/lookupTypes";
import { StandardJobCodeType } from "components/QueueBoard/models/workOrderTypes";
import type {
  SettingsLocationBusinessHours,
  SettingsLocationHoliday,
  EmployeeSchedule,
  EmployeeScheduleException,
} from "../../technicianCalendar/lib/models";

// Get username from localStorage for cache operations
const getUsername = (): string => {
  return localStorage.getItem("username") || "";
};

export const useLookupData = () => {
  const [username, setUsername] = useState<string>(getUsername());
  const {
    setLocationLookup,
    setTechnicianLookup,
    setTechnicianLookupForFilters,
    setServiceCodesLookup,
    setStandardJobCodesLookup,
    setWoStatusLookup,
    setWoPriorityListLookup,
    setSalesPersonsLookup,
    setStandardJobSourceLookup,
    setCustomerListHeadersMyList,
    setCustomerListHeadersPicklist,
    setPaymentMethods,
    setAltStockLocations,
    setPartListHeaders,
    setUnitListHeadersMyList,
    setUnitListHeadersPicklist,
    setMiscCharges,
    setZones,
    setCustomerZones,
    setCountriesLookup,
    setStatesLookup,
    setWoDocStatusesLookup,
  } = useWorkOrderStore();

  // Scheduling data for technician calendar is cached here but loaded in use-schedule.ts
  // This hook only handles caching, not setting in any store

  const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours
  useEffect(() => {
    const handleUserChanged = () => {
      setUsername(getUsername());
    };

    window.addEventListener("userChanged", handleUserChanged);
    window.addEventListener("storage", handleUserChanged);
    return () => {
      window.removeEventListener("userChanged", handleUserChanged);
      window.removeEventListener("storage", handleUserChanged);
    };
  }, []);

  useEffect(() => {
    if (!username) {
      return;
    }

    const fetchAllLookups = async () => {
      try {
        await Promise.all([
          updateLookup<SettingsLocation>(
            "settingsLocations",
            LookupAPI.getLocations,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setLocationLookup(data)),

          updateLookup<ApplicationUser>(
            "techniciansForFilters",
            LookupAPI.getTechniciansForFilters,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setTechnicianLookupForFilters(data)),

          updateLookup<ApplicationUser>(
            "technicians",
            LookupAPI.getTechnicians,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setTechnicianLookup(data)),

          updateLookup<WorkOrderStatus>(
            "WorkOrderStatuses",
            LookupAPI.getWorkOrderStatuses,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setWoStatusLookup(data)),

          updateLookup<WorkOrderPriorityCode>(
            "workOrderPriorityList",
            LookupAPI.getWorkOrderPriorityList,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setWoPriorityListLookup(data)),

          updateLookup<ApplicationUser>(
            "salesPersonList",
            LookupAPI.getSalesPersons,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setSalesPersonsLookup(data)),

          updateLookup<string>(
            "standardJobSourceList",
            LookupAPI.getStandardJobSourceList,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setStandardJobSourceLookup(data)),

          updateLookup<ServiceCodes>(
            "serviceCodeList",
            LookupAPI.getServiceCodes,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setServiceCodesLookup(data)),

          updateLookup<StandardJobCodeType>(
            "standardJobCodeTypeList",
            LookupAPI.getStandardJobCodeType,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setStandardJobCodesLookup(data)),

          // Customer Lists - fetch all at once
          updateLookupRecord<CustomerListHeader>(
            "allCustomerListHeaders",
            () => LookupAPI.getAllCustomerListHeaders(),
            username,
            MAX_CACHE_AGE,
          )
            .then((data) => {
              setCustomerListHeadersMyList(data["mylist"] || []);
              setCustomerListHeadersPicklist(data["picklist"] || []);
            })
            .catch((error) => {
              console.error("Error fetching Customer Lists:", error);
            }),

          // Payment Methods
          updateLookup<PaymentMethod>(
            "paymentMethods",
            LookupAPI.getPaymentMethods,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setPaymentMethods(data)),

          // Alt Stock Locations
          updateLookup<AltStockLocation>(
            "altStockLocations",
            LookupAPI.getAltStockLocations,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setAltStockLocations(data)),

          // Part Lists - fetch all at once
          updateLookupRecord<PartListHeader>(
            "allPartListHeaders",
            () => LookupAPI.getAllPartListHeaders(),
            username,
            MAX_CACHE_AGE,
          )
            .then((data) => {
              //console.log("All Part List data fetched:", data);
              // Store each source type in the partListHeaders dictionary
              Object.keys(data).forEach((source) => {
                setPartListHeaders(source, data[source] || []);
              });
            })
            .catch((error) => {
              //console.error("Error fetching Part Lists:", error);
            }),

          // Misc Charges
          updateLookup<MiscCharge>(
            "miscCharges",
            LookupAPI.getMiscCharges,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setMiscCharges(data)),

          // Zones
          updateLookup<Zone>(
            "zones",
            LookupAPI.getZones,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setZones(data)),

          updateLookup<Zone>(
            "customerZones",
            LookupAPI.getCustomerZone,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setCustomerZones(data)),

          updateLookup<Country>(
            "countries",
            LookupAPI.getCountries,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setCountriesLookup(data)),

          updateLookup<State>(
            "states",
            LookupAPI.getStates,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setStatesLookup(data)),

          updateLookup<string>(
            "woDocStatuses",
            LookupAPI.getWoDocumentStatuses,
            username,
            MAX_CACHE_AGE,
          ).then((data) => setWoDocStatusesLookup(data)),

          // Unit Lists - fetch all at once
          updateLookupRecord<UnitListHeader>(
            "allUnitListHeaders",
            () => LookupAPI.getAllUnitListHeaders(),
            username,
            MAX_CACHE_AGE,
          )
            .then((data) => {
              //console.log("All Unit List data fetched:", data);
              setUnitListHeadersMyList(data["myunitlist"] || []);
              setUnitListHeadersPicklist(data["unitlist"] || []);
            })
            .catch((error) => {
              console.error("Error fetching Unit Lists:", error);
            }),

          // Scheduling data for Technician Calendar (cached here; loaded in use-schedule.ts
          // and page.tsx). Cache cleared on each login (clearSchedulingLookupCaches) so
          // the next load always refetches; MAX_CACHE_AGE applies for the rest of the session.
          updateLookup<SettingsLocationBusinessHours>(
            "sch_locationBusinessHours",
            () => LookupAPI.getAllLocationBusinessHours(),
            username,
            MAX_CACHE_AGE,
          ),

          updateLookup<SettingsLocationHoliday>(
            "sch_locationHolidays",
            () => LookupAPI.getAllLocationHolidays(),
            username,
            MAX_CACHE_AGE,
          ),

          updateLookup<EmployeeSchedule>(
            "sch_employeeSchedules",
            () => LookupAPI.getAllEmployeeSchedules(),
            username,
            MAX_CACHE_AGE,
          ),

          updateLookup<EmployeeScheduleException>(
            "sch_employeeScheduleExceptions",
            () => LookupAPI.getAllEmployeeScheduleExceptions(),
            username,
            MAX_CACHE_AGE,
          ),
        ]);
      } catch (error) {
        console.error("Error fetching lookup data:", error);
      }
    };

    fetchAllLookups();
  }, [
    setLocationLookup,
    setTechnicianLookup,
    setTechnicianLookupForFilters,
    setServiceCodesLookup,
    setStandardJobCodesLookup,
    setWoStatusLookup,
    setWoPriorityListLookup,
    setSalesPersonsLookup,
    setStandardJobSourceLookup,
    setCustomerListHeadersMyList,
    setCustomerListHeadersPicklist,
    setPaymentMethods,
    setAltStockLocations,
    setPartListHeaders,
    setUnitListHeadersMyList,
    setUnitListHeadersPicklist,
    setMiscCharges,
    setZones,
    setCustomerZones,
    setCountriesLookup,
    setStatesLookup,
    setWoDocStatusesLookup,
    username,
  ]);
};
